import { FunctionComponent, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import AppLogo from "@/assets/images/favicon.png";

import { ROUTE_CONFIG } from "@/routes/config";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";

import { useFinalizeCheckoutSessionMutation } from "@/pages/Private/redux/subscription/subscription.api";
import {
	useCompleteOnboardingMutation,
	useUpdateMeMutation,
} from "@/pages/Private/redux/account/account.api";

import { skipsCheckout, useOnboardingVariant } from "@/utils/useOnboardingVariant";

export const OnboardingDone: FunctionComponent = () => {
	const navigate = useNavigate();

	const { t } = useTranslation();
	const ts = (key: string) => t(`onboarding.done.${key}`);

	const [searchParams, setSearchParams] = useSearchParams();

	const [error, setError] = useState<string | null>(null);

	const [finalizeCheckoutSession, { isLoading: isFinalizingCheckoutSession }] =
		useFinalizeCheckoutSessionMutation();

	useEffect(() => {
		const sessionId = searchParams.get("session_id");

		if (sessionId) {
			finalizeCheckoutSession({ sessionId })
				.unwrap()
				.then(() => {
					setSearchParams({}, { replace: true });
				})
				.catch((err: unknown) => {
					console.error(err);
				});
			setSearchParams({}, { replace: true });
		}
	}, [searchParams, finalizeCheckoutSession, setSearchParams]);

	const [updateMe, { isLoading: isUpdatingMe }] = useUpdateMeMutation();

	// V2 has no checkout to finalize (PREC-3719), but the workspace still has to be
	// started — the lists the wizard created and the lead-scoring generation both
	// hang off that call. It runs before the user is marked onboarded, so a failure
	// leaves them on this screen able to press the button again.
	const skipCheckout = skipsCheckout(useOnboardingVariant());
	const [completeOnboarding, { isLoading: isCompletingOnboarding }] =
		useCompleteOnboardingMutation();

	const handleUpdateMe = async () => {
		setError(null);

		try {
			if (skipCheckout) {
				await completeOnboarding().unwrap();
			}

			await updateMe({
				isOnboarded: true,
			}).unwrap();

			navigate(ROUTE_CONFIG.DASHBOARD);
		} catch (err) {
			console.error(err);
			setError(ts("startFailed"));
		}
	};

	const renderHeader = (
		<>
			<img alt="logo" className="w-[48px] h-[48px] mb-4" src={AppLogo} />
			<div className="text-center text-md font-bold mb-4 mx-[94px]">All set.</div>
			<div className="text-center text-ssm font-normal mb-8 text-text-meta mx-[94px]">
				<span className="text-transparent bg-gradient-to-r from-[#1D3DF2] to-[#B771EE] bg-clip-text font-bold">
					Welcome to Signalist!
				</span>
				<br />
				We’re personalising your lead lists in the background and will email you when they’re ready
				— jump into your dashboard to get started right away.
			</div>
		</>
	);

	return (
		<div className={`w-full flex flex-col items-center justify-center `}>
			<div className="flex flex-col bg-white px-6 py-10 rounded-[20px] w-[656px] max-w-[100%] shadow-[0px_0px_80px_0px_rgba(171,178,217,0.35)] items-center justify-center mb-6">
				{renderHeader}

				<div className="w-auto">
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						isLoading={isFinalizingCheckoutSession || isUpdatingMe || isCompletingOnboarding}
						size={ButtonSize.ML}
						title="Go to Dashboard"
						onClick={handleUpdateMe}
					/>
				</div>

				{error && (
					<div className="mt-4 text-center text-ssm font-normal text-status-error">{error}</div>
				)}
			</div>
		</div>
	);
};
